import { useEffect, useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { BookOpen, MapPin, Package, Store, UserCog, Users } from 'lucide-react'
import { getControlAccounts, getCustomers, getLocations, getProducts, getSetupStatus, getUsers, getVendors } from '../../api/master'
import { Card, Tile } from '../../components/Summary'
import { MasterTabs, isService, useMasterAccess, userActive } from './masterShared'

// Shared transaction accounts every company needs before it can post (see Transaction accounts).
const REQUIRED_DEFAULTS = ['Cash', 'Card', 'AR', 'Inventory', 'Sales', 'SalesDiscounts', 'COGS', 'AP', 'AccruedPurchases', 'SalesTaxPayable',
  'DeferredRevenue', 'RetainedEarnings', 'PPE', 'AccumDep', 'DepreciationExpense', 'PrepaidAsset', 'PrepaidExpense', 'InventoryLoss', 'InventoryGain']

const settle = (p) => p.then((v) => (Array.isArray(v) ? v : [])).catch(() => [])

// Master files summary: how many of each record there are, and anything
// that needs filling in.
export default function MasterMenu() {
  const nav = useNavigate()
  const can = useMasterAccess()
  const [data, setData] = useState(null)

  // A new company goes to account setup first.
  useEffect(() => {
    if (!can.accounts) return
    getSetupStatus().then((st) => { if (!st?.has_accounts || !st?.has_control_mappings) nav('/master/setup') }).catch(() => {})
  }, [can.accounts, nav])

  useEffect(() => {
    let cancelled = false
    Promise.all([
      can.items ? settle(getProducts()) : [],
      can.customers ? settle(getCustomers()) : [],
      can.vendors ? settle(getVendors()) : [],
      can.locations ? settle(getLocations()) : [],
      can.users ? settle(getUsers()) : [],
      can.accounts ? settle(getControlAccounts()) : [],
    ]).then(([items, customers, vendors, locations, users, defaults]) => {
      if (!cancelled) setData({ items, customers, vendors, locations, users, defaults })
    })
    return () => { cancelled = true }
  }, [can.items, can.customers, can.vendors, can.locations, can.users, can.accounts])

  const n = (k) => (data ? data[k].length.toLocaleString() : '…')
  const services = data ? data.items.filter(isService).length : 0
  const noPrice = data ? data.items.filter((i) => !(Number(i.unit_price) > 0)) : []
  const negative = data ? data.items.filter((i) => !isService(i) && Number(i.qty_on_hand) < 0) : []
  const mapped = data ? new Set(data.defaults.filter((d) => String(d.account_code || '').trim()).map((d) => d.name)) : new Set()
  const unmapped = data && can.accounts ? REQUIRED_DEFAULTS.filter((k) => !mapped.has(k)) : []
  const issues = [
    noPrice.length > 0 && { key: 'price', text: `${noPrice.length} ${noPrice.length === 1 ? 'product or service has' : 'products and services have'} no selling price`, to: '/master/products?show=noprice' },
    negative.length > 0 && { key: 'neg', text: `${negative.length} ${negative.length === 1 ? 'product shows' : 'products show'} less than zero on hand`, to: '/inventory/stock' },
    unmapped.length > 0 && { key: 'acct', text: `${unmapped.length} transaction ${unmapped.length === 1 ? 'account isn’t' : 'accounts aren’t'} set`, to: '/master/control-accounts' },
  ].filter(Boolean)

  return (
    <div className="section-page">
      <MasterTabs />
      <header className="section-header"><h1>Master files</h1></header>

      <div className="sum-tiles">
        {can.items && <Tile label="Products & services" href="/master/products" icon={Package} value={n('items')} note={data ? `${services} ${services === 1 ? 'service' : 'services'}` : ''} />}
        {can.customers && <Tile label="Customers" href="/master/customers" icon={Users} value={n('customers')} />}
        {can.vendors && <Tile label="Vendors" href="/master/vendors" icon={Store} value={n('vendors')} />}
        {can.locations && <Tile label="Locations" href="/master/locations" icon={MapPin} value={n('locations')} />}
        {can.users && <Tile label="Users" href="/master/users" icon={UserCog} value={n('users')} note={data ? `${data.users.filter(userActive).length} active` : ''} />}
        {can.accounts && <Tile label="Transaction accounts" href="/master/control-accounts" icon={BookOpen} value={data ? (unmapped.length ? `${unmapped.length} to set` : 'All set') : '…'} tone={unmapped.length ? 'warn' : undefined} />}
      </div>

      <div className="sum-columns">
        <div className="sum-main">
          <Card title="Needs attention">
            {!data ? <p className="sum-muted">Checking…</p> : issues.length ? (
              <ul className="sum-list">
                {issues.map((i) => <li key={i.key}><span className="sum-list-main"><Link to={i.to}>{i.text}</Link></span></li>)}
              </ul>
            ) : <p className="sum-muted">Nothing to fix.</p>}
          </Card>
        </div>
        {can.vendors && (
          <div className="sum-side">
            <Card title="Add many at once">
              <p className="sum-muted">Bring in products, customers, vendors and more from a spreadsheet.</p>
              <p><Link to="/import">Import from a spreadsheet</Link></p>
            </Card>
          </div>
        )}
      </div>
    </div>
  )
}
